import { MappedField } from 'libs/datagrid/src/lib/containers/dx-datagrid/dx-datagrid.types';
import { hasAllowedFields } from './field-structure.utils';

export type InvoicingFields = {
  /**
   * Required for commit form
   */
  controlType?: InvoicingPlaceHolderTypeVal;
  /**
   * Shows dataType tah
   */
  dataType?: InvoicingPlaceHolderTypeVal;
  editType?: string;
  /**
   * Shows if field should be excluded in specific forms
   */
  exclude?: InvoicingPlaceHolderTypeVal;
  exclusive?: string;
  format?: string;
  /**
   * Shows the name that should be displayed in forms
   */
  headerText?: InvoicingPlaceHolderTypeVal;

  /**
   * Used as field in forms
   */
  id: InvoicingPlaceHolderTypeVal;
  /**
   * (Datagrid)
   */
  isPrimaryKey?: boolean;
  /**
   * Shows if the field is mandatory in forms
   */
  isRequired?: InvoicingPlaceHolderTypeVal;
  /**
   * Adds field explicitly in datagrid columns
   * */
  mandatory?: boolean;

  /**
   * Shows if it should be visible by default in forms
   */
  visible?: InvoicingPlaceHolderTypeVal;
};

export type InvoicingPlaceHolderTypeVal = {
  createForm?: boolean | number | string;
  datagrid?: boolean | number | string;
  editForm?: boolean | number | string;
  nestedBuyerForm?: boolean | number | string;
  nestedForm?: boolean | number | string; //use nested<component>form for specific Component
  nestedNotificationForm?: boolean | number | string;
};

export enum EnumInvoicingFieldStructure {
  InvoicingTable = 0,
}

export class InvoicingList {
  fields: MappedField[];
  userRightFields!: string[];
  hasField: (
    field: InvoicingFields,
    refId: keyof InvoicingPlaceHolderTypeVal,
    actualId: keyof InvoicingPlaceHolderTypeVal,
  ) => boolean;

  constructor(type?: EnumInvoicingFieldStructure, userRightFields?: string[]) {
    if (hasAllowedFields(userRightFields)) {
      this.userRightFields = userRightFields.map((e): string => e.toLowerCase());
      this.hasField = this.validFunWithUserFields;
    } else {
      this.hasField = this.validFunWithoutUserFields;
    }
    this.fields = this.getDefaultFields(type);
  }

  validFunWithUserFields = (
    field: InvoicingFields,
    refId: keyof InvoicingPlaceHolderTypeVal,
    actualId: keyof InvoicingPlaceHolderTypeVal,
  ): boolean => {
    if (
      this.userRightFields &&
      !(field.isRequired && field.isRequired[actualId]) &&
      !field.mandatory //For adding fields not present in default fields
    ) {
      return (
        this.userRightFields.includes((<string>field.id[refId]).toLowerCase()) &&
        !(field.exclude && field.exclude[actualId] == true)
      );
    } else {
      return this.validFunWithoutUserFields(field, refId, actualId);
    }
  };

  validFunWithoutUserFields = (
    field: InvoicingFields,
    refId: keyof InvoicingPlaceHolderTypeVal,
    actualId: keyof InvoicingPlaceHolderTypeVal,
  ): boolean => {
    if (field.exclude && field.exclude[actualId] == true) {
      return false;
    } else {
      return true;
    }
  };

  getDefaultFields(type?: EnumInvoicingFieldStructure): MappedField[] {
    switch (type) {
      case EnumInvoicingFieldStructure.InvoicingTable:
        return this.getDatagridFields();
      default:
        return this.getDatagridFields();
    }
  }

  getDatagridFields(): MappedField[] {
    return this.invoicingList
      .filter((field: InvoicingFields): boolean => {
        return this.hasField(field, 'datagrid', 'datagrid');
      })
      .map((field: InvoicingFields): MappedField => {
        return {
          field: (field.id?.datagrid as string) ?? '',
          headerText: (field.headerText?.datagrid as string) ?? '',
          isRequired: !!field.isRequired?.datagrid,
          mandatory: !!field.mandatory,
          type: field.dataType?.datagrid ? (field.dataType.datagrid as string) : 'string',
          visible: !!field.visible?.datagrid,
        };
      });
  }

  public readonly invoicingList: InvoicingFields[] = [
    {
      dataType: { datagrid: 'status' },
      headerText: { datagrid: 'Status' },
      id: { datagrid: 'status' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Type' },
      id: { datagrid: 'type' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Plant' },
      id: { datagrid: 'plant' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Vendor Code' },
      id: { datagrid: 'vendorCode' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Invoice Number' },
      id: { datagrid: 'invoiceNumber' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'dateUnchanged' },
      headerText: { datagrid: 'Invoice Date' },
      id: { datagrid: 'invoiceDate' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'dateUnchanged' },
      headerText: { datagrid: 'Due Date' },
      id: { datagrid: 'dueDate' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Amount Include VAT' },
      id: { datagrid: 'amountIncludeVat' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Currency' },
      id: { datagrid: 'currency' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'dateUnchanged' },
      headerText: { datagrid: 'Clearing Date' },
      id: { datagrid: 'clearingDate' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'dateUnchanged' },
      headerText: { datagrid: 'Issue Date' },
      id: { datagrid: 'issueDate' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'PBk' },
      id: { datagrid: 'pBk' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'dateUnchanged' },
      headerText: { datagrid: 'Payment Schedule' },
      id: { datagrid: 'paymentSchedule' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Text' },
      id: { datagrid: 'text' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'r-flag' },
      headerText: { datagrid: 'Reviewed' },
      id: { datagrid: 'flags' },
      isRequired: { datagrid: true },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'dateUnchanged' },
      headerText: { datagrid: 'Received' },
      id: { datagrid: 'received' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'dateUnchanged' },
      headerText: { datagrid: 'Reviewed' },
      id: { datagrid: 'reviewed' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'PurchOrg' },
      id: { datagrid: 'purchasingOrganization' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'downloadExcel' },
      headerText: { datagrid: 'Download XLSX' },
      id: { datagrid: 'downloadExcel' },
      mandatory: true,
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'downloadPdf' },
      headerText: { datagrid: 'Download PDF' },
      id: { datagrid: 'downloadPDF' },
      mandatory: true,
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'downloadXml' },
      headerText: { datagrid: 'Download XML' },
      id: { datagrid: 'downloadXML' },
      mandatory: true,
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'DocumentFI' },
      id: { datagrid: 'documentFI' },
      mandatory: true,
      visible: { datagrid: true },
    },
  ];
}
